import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingContact from "../components/FloatingContact";
import SEOHead from "../components/SEOHead";
import { useLanguage } from "../contexts/LanguageContext";
import { useSiteSettings } from "../contexts/SiteSettingsContext";

const SITE_URL = "https://adrise-agency.com";

const TEXT = {
    en: {
        title: "Contact",
        subtitle: "Commercial and support channels for active and prospective clients.",
        seoTitle: "Contact Adrise — Sales and Support",
        seoDescription:
            "Contact Adrise through official email and Telegram support channels for onboarding, billing, and account operations.",
        emailTitle: "Email support",
        tgTitle: "Telegram support",
        legal: "For legal/privacy requests, include your account email and relevant order or transaction reference.",
    },
    pt: {
        title: "Contato",
        subtitle: "Canais comerciais e de suporte para clientes atuais e novos.",
        seoTitle: "Contato Adrise — Vendas e Suporte",
        seoDescription:
            "Fale com a Adrise por email e Telegram para onboarding, cobrança e operações de conta.",
        emailTitle: "Suporte por e-mail",
        tgTitle: "Suporte no Telegram",
        legal: "Para solicitações legais/privacidade, informe o e-mail da conta e referência do pedido/transação.",
    },
    es: {
        title: "Contacto",
        subtitle: "Canales comerciales y de soporte para clientes activos y nuevos.",
        seoTitle: "Contacto Adrise — Ventas y Soporte",
        seoDescription:
            "Contacta con Adrise por email y Telegram para onboarding, facturación y operaciones de cuenta.",
        emailTitle: "Soporte por email",
        tgTitle: "Soporte por Telegram",
        legal: "Para solicitudes legales/privacidad, incluye email de cuenta y referencia de pedido/transacción.",
    },
    ru: {
        title: "Контакты",
        subtitle: "Коммерческие и сервисные каналы для текущих и новых клиентов.",
        seoTitle: "Контакты Adrise — Продажи и Поддержка",
        seoDescription:
            "Свяжитесь с Adrise через официальный email и Telegram по вопросам онбординга, биллинга и операций аккаунта.",
        emailTitle: "Поддержка по email",
        tgTitle: "Поддержка в Telegram",
        legal: "Для юридических/приватных запросов укажите email аккаунта и ID заказа/транзакции.",
    },
    tr: {
        title: "İletişim",
        subtitle: "Mevcut ve yeni müşteriler için ticari ve destek kanalları.",
        seoTitle: "Adrise İletişim — Satış ve Destek",
        seoDescription:
            "Onboarding, faturalama ve hesap operasyonları için Adrise’a resmi e-posta ve Telegram kanallarından ulaşın.",
        emailTitle: "E-posta desteği",
        tgTitle: "Telegram desteği",
        legal: "Hukuki/gizlilik taleplerinde hesap e-postanızı ve ilgili sipariş/işlem referansını ekleyin.",
    },
};

export default function ContactPage() {
    const { language } = useLanguage();
    const { settings } = useSiteSettings();
    const c = TEXT[language] || TEXT.en;

    const email = settings?.contact_email || "support@adrise-agency.com";
    const telegram = settings?.contact_telegram || "https://t.me/adrise";
    const phone = settings?.contact_phone || "";
    const address = settings?.company_address || "";
    const legalName = settings?.company_legal_name || settings?.company_name || "Adrise";

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: c.title,
        url: `${SITE_URL}/contact`,
        inLanguage: language,
        isPartOf: { "@type": "WebSite", name: "Adrise", url: SITE_URL },
        mainEntity: {
            "@type": "Organization",
            name: "Adrise",
            email,
            telephone: phone || undefined,
            sameAs: [telegram],
            address: address
                ? {
                      "@type": "PostalAddress",
                      streetAddress: address,
                  }
                : undefined,
        },
    };

    return (
        <main data-testid="contact-page" className="bg-background text-foreground min-h-screen">
            <SEOHead
                title={c.seoTitle}
                description={c.seoDescription}
                canonical={`${SITE_URL}/contact`}
                keywords={[
                    "adrise contact",
                    "agency ad accounts support",
                    "telegram support",
                    "billing support",
                ]}
                jsonLd={jsonLd}
                alternates={[
                    { hrefLang: "en", href: `${SITE_URL}/contact` },
                    { hrefLang: "pt", href: `${SITE_URL}/contact` },
                    { hrefLang: "es", href: `${SITE_URL}/contact` },
                    { hrefLang: "ru", href: `${SITE_URL}/contact` },
                    { hrefLang: "tr", href: `${SITE_URL}/contact` },
                    { hrefLang: "x-default", href: `${SITE_URL}/contact` },
                ]}
            />

            <Navbar />

            <section className="pt-32 md:pt-40 pb-16" data-testid="contact-hero-section">
                <div className="container-skala">
                    <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight">
                        {c.title}
                    </h1>
                    <p className="text-sm md:text-base text-muted-foreground mt-4 max-w-3xl">
                        {c.subtitle}
                    </p>
                </div>
            </section>

            <section className="pb-20" data-testid="contact-content-section">
                <div className="container-skala max-w-4xl grid md:grid-cols-2 gap-5">
                    <article className="md:col-span-2 bg-card border border-white/[0.08] rounded-2xl p-6 md:p-7" data-testid="contact-company-card">
                        <h2 className="text-base md:text-lg font-semibold tracking-tight">{legalName}</h2>
                        <p className="text-sm md:text-base text-muted-foreground mt-2 leading-relaxed">
                            {settings?.service_scope && !/^managed advertising operations/i.test(settings.service_scope) ? settings.service_scope : ({ tr: "Yönetilen reklam operasyonları · Medya satın alma desteği · Faturalama ve hesap yönetimi", es: "Operaciones publicitarias gestionadas · Soporte de media buying · Facturación y gestión de cuentas", pt: "Operações de publicidade gerenciadas · Suporte de media buying · Cobrança e gestão de contas", ru: "Управляемые рекламные операции · Поддержка медиабаинга · Биллинг и ведение аккаунтов" })[language] || "Managed advertising operations · Media buying support · Billing & account management"}
                        </p>
                    </article>

                    <article className="bg-card border border-white/[0.08] rounded-2xl p-6 md:p-7" data-testid="contact-email-card">
                        <h2 className="text-base md:text-lg font-semibold tracking-tight">{c.emailTitle}</h2>
                        <a
                            href={`mailto:${email}`}
                            data-testid="contact-email-link"
                            className="inline-block mt-3 text-sm md:text-base text-primary hover:text-primary/90 break-all"
                        >
                            {email}
                        </a>
                    </article>

                    <article className="bg-card border border-white/[0.08] rounded-2xl p-6 md:p-7" data-testid="contact-telegram-card">
                        <h2 className="text-base md:text-lg font-semibold tracking-tight">{c.tgTitle}</h2>
                        <a
                            href={telegram}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="contact-telegram-link"
                            className="inline-block mt-3 text-sm md:text-base text-primary hover:text-primary/90 break-all"
                        >
                            {telegram}
                        </a>
                    </article>

                    <article className="bg-card border border-white/[0.08] rounded-2xl p-6 md:p-7" data-testid="contact-phone-card">
                        <h2 className="text-base md:text-lg font-semibold tracking-tight">
                            {language === "tr" ? "Telefon" : language === "ru" ? "Телефон" : language === "es" ? "Teléfono" : language === "pt" ? "Telefone" : "Phone"}
                        </h2>
                        <p data-testid="contact-phone-value" className="inline-block mt-3 text-sm md:text-base text-muted-foreground break-all">
                            {phone || ({ tr: "Yakında eklenecek", es: "Próximamente", pt: "Em breve", ru: "Скоро" })[language] || "Coming soon"}
                        </p>
                    </article>

                    <article className="bg-card border border-white/[0.08] rounded-2xl p-6 md:p-7" data-testid="contact-address-card">
                        <h2 className="text-base md:text-lg font-semibold tracking-tight">
                            {language === "tr" ? "Adres" : language === "ru" ? "Адрес" : language === "es" ? "Dirección" : language === "pt" ? "Endereço" : "Address"}
                        </h2>
                        <p data-testid="contact-address-value" className="inline-block mt-3 text-sm md:text-base text-muted-foreground whitespace-pre-line">
                            {address || ({ tr: "Yakında eklenecek", es: "Próximamente", pt: "Em breve", ru: "Скоро" })[language] || "Coming soon"}
                        </p>
                    </article>

                    <article className="md:col-span-2 bg-card border border-white/[0.08] rounded-2xl p-6 md:p-7" data-testid="contact-legal-note-card">
                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">{c.legal}</p>
                        <p className="text-xs text-muted-foreground/80 mt-3" data-testid="contact-guarantee-note">
                            {settings?.guarantee_note && !/^platform approvals and account status/i.test(settings.guarantee_note) ? settings.guarantee_note : ({ tr: "Platform onayları ve hesap durumu platform politikalarına tabidir.", es: "Las aprobaciones y el estado de las cuentas están sujetos a las políticas de cada plataforma.", pt: "Aprovações e status das contas estão sujeitos às políticas das plataformas.", ru: "Одобрения платформ и статус аккаунтов зависят от политик платформ." })[language] || "Platform approvals and account status are subject to platform policies."}
                        </p>
                    </article>
                </div>
            </section>

            <FloatingContact />
            <Footer />
        </main>
    );
}
